import React, { useEffect, useMemo, useCallback } from 'react';
import { Box, Flex, TextV2, ButtonV2, TooltipV2, IconV2, DialogV2 } from '@withjoy/joykit';
import { ReactComponent as Info } from '@assets/icons/info.svg';
import { ReactComponent as Warning } from '@assets/icons/Error-32.svg';
import { CheckOutline } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { ReactComponent as AllyLogo2 } from '@assets/icons/ally-logo.svg';
import { pxToRem } from '@withjoy/joykit/theme';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { transferDialogOverrides, TransferDialogBody, PromoCard, PromoHeadline, PromoSubhead, PromoBullet, PromoOpenButton } from './TransferToBankDialog.styles';
import { TRANSFER_FEE } from './constants';
import type { TransferToBankDialogProps, PayoutStatus } from './TransferToBankDialog.types';

const ALLY_URL = 'https://www.ally.com/';

const AllyPromoBlock: React.FC<{
  onOpenAccountClicked?: () => void;
}> = ({ onOpenAccountClicked }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');

  const handleOpenAccount = useCallback(() => {
    onOpenAccountClicked?.();
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
  }, [onOpenAccountClicked, openWindow]);

  return (
    <PromoCard
      flexDirection="column"
      padding={[6, 5]}
      gap={6}
      alignItems="flex-start"
    >
      <Flex
        flexDirection={['column', 'row']}
        gap={6}
        alignItems={['center', 'flex-start']}
        width="100%"
      >
        <Box
          flexShrink={0}
          aria-hidden
        >
          <Box
            as={AllyLogo2}
            style={{ height: 30, width: 'auto' }}
          />
        </Box>
        <Flex
          flexDirection="column"
          gap={[4, 2]}
          alignItems="flex-start"
          flex={1}
          minWidth={0}
          width={['100%', 'auto']}
        >
          <Flex
            flexDirection="column"
            gap={[2, 1]}
            paddingBottom={[4, pxToRem(10)]}
          >
            <PromoHeadline>{tq`Level up your savings game together.`}</PromoHeadline>
            <PromoSubhead>{tq`Open a joint account with Ally Bank.`}</PromoSubhead>
          </Flex>
          <Flex
            flexDirection="column"
            gap={[4, 3]}
          >
            <Flex
              alignItems="center"
              gap={2}
            >
              <IconV2
                size="sm"
                color="white"
              >
                <CheckOutline />
              </IconV2>
              <PromoBullet>{tq`Competitive, variable rates for savings`}</PromoBullet>
            </Flex>
            <Flex
              alignItems="center"
              gap={2}
            >
              <IconV2
                size="sm"
                color="white"
              >
                <CheckOutline />
              </IconV2>
              <PromoBullet>{tq`ZERO-monthly maintenance fees`}</PromoBullet>
            </Flex>
            <Flex
              alignItems="center"
              gap={2}
            >
              <IconV2
                size="sm"
                color="white"
              >
                <CheckOutline />
              </IconV2>
              <PromoBullet>{tq`Get $xx Instantly on sign-up!`}</PromoBullet>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <PromoOpenButton
        size="lg"
        shape="rounded"
        onClick={handleOpenAccount}
        backgroundColor="white"
        color="mono14"
        width={['100%', pxToRem(304)]}
        maxWidth="100%"
        marginX={[0, 7]}
      >
        {tq`Open Account`}
      </PromoOpenButton>
    </PromoCard>
  );
};

const StatusBody: React.FC<{
  status: PayoutStatus;
  onClose: () => void;
  onStatusCloseClicked?: (status: PayoutStatus) => void;
}> = ({ status, onClose, onStatusCloseClicked }) => {
  const { tq } = useTranslation('transferToBankDialog');

  const handleClose = useCallback(() => {
    onStatusCloseClicked?.(status);
    onClose();
  }, [status, onClose, onStatusCloseClicked]);

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        marginBottom={5}
      >
        <IconV2
          size="xxl"
          color={status === 'success' ? 'mono14' : 'negative6'}
        >
          {status === 'success' ? <CheckOutline /> : <Warning />}
        </IconV2>
        <TextV2
          textAlign="center"
          typographyVariant="display1"
          color="mono14"
        >
          {status === 'success' ? tq`Your Transfer is Being Proccessed` : tq`Oops! Something Went Wrong.`}
        </TextV2>
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Flex
          justifyContent="center"
          marginBottom={8}
        >
          <TextV2
            textAlign="center"
            typographyVariant="body3"
            color="mono12"
          >
            {status === 'success'
              ? tq`Woo-hoo! We're working on moving your cash fund balance to your bank account now. Please note that transferred funds typically arrive within 5 to 7 business days.`
              : tq`We're unable to complete this transfer. Please ensure the destination account is valid and try again. If this error persists, contact Joy Customer Support.`}
          </TextV2>
        </Flex>
        <Box>
          <ButtonV2
            onClick={handleClose}
            size="lg"
            intent="neutral"
            shape="rounded"
            variant="outline"
          >
            {tq`Close`}
          </ButtonV2>
        </Box>
      </Flex>
    </>
  );
};

export const TransferToBankDialogView: React.FC<TransferToBankDialogProps> = ({
  isOpen,
  onClose,
  availableBalance,
  currencyCode,
  onConnectBank,
  payoutFormProps,
  payoutStatus,
  telemetry,
}) => {
  const { tq } = useTranslation('transferToBankDialog');
  const { formatCurrency } = useCurrencyFormatter();
  const [enableAllyPromo] = useIsFeatureEnabled('enableAllyPromo');
  const [enableAllyPromoTransferToBank] = useIsFeatureEnabled('enableAllyPromoTransferToBank');
  const showAllyTransferPromo = enableAllyPromo && enableAllyPromoTransferToBank;

  const isBankNotConnected = !payoutFormProps;
  const isPendingView = !!payoutFormProps && payoutStatus === 'pending';
  const showConnectBankView = isBankNotConnected;
  const showDialogTitle = showConnectBankView || isPendingView;
  const showStatusView = !!payoutFormProps && !!payoutStatus && payoutStatus !== 'pending';

  useEffect(() => {
    if (isOpen && showConnectBankView) {
      telemetry?.onConnectBankViewViewed?.();
    }
  }, [isOpen, showConnectBankView, telemetry]);

  useEffect(() => {
    if (isOpen && isPendingView) {
      telemetry?.onPendingViewViewed?.();
    }
  }, [isOpen, isPendingView, telemetry]);

  const { formattedBalance, feeFormatted, totalFormatted } = useMemo(() => {
    const balance = availableBalance?.valueInMajorUnits ?? 0;
    const totalTransferValue = availableBalance?.valueInMajorUnits ? availableBalance.valueInMajorUnits - TRANSFER_FEE : 0;
    return {
      formattedBalance: formatCurrency({
        priceFloatingPointDecimalString: balance.toString(),
        priceCurrencyCode: availableBalance?.currencyCode ?? currencyCode,
        formatForm: 'auto',
      }),
      feeFormatted: formatCurrency({
        priceFloatingPointDecimalString: TRANSFER_FEE.toString(),
        priceCurrencyCode: availableBalance?.currencyCode ?? currencyCode,
        formatForm: 'auto',
      }),
      totalFormatted: formatCurrency({
        priceFloatingPointDecimalString: totalTransferValue.toString(),
        priceCurrencyCode: availableBalance?.currencyCode ?? currencyCode,
        formatForm: 'auto',
      }),
    };
  }, [formatCurrency, availableBalance, currencyCode]);

  const renderBalanceAndFeeBlock = () => (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={1}
        paddingBottom={6}
      >
        <TextV2
          typographyVariant="display2"
          color="mono14"
        >
          {formattedBalance}
        </TextV2>
        <TextV2
          typographyVariant="hed1"
          color="mono14"
        >
          {tq`Transferrable Balance`}
        </TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        gap={5}
        width="100%"
        marginBottom={8}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex
            alignItems="center"
            gap={1}
          >
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {tq`Transfer Fee`}
            </TextV2>
            <TooltipV2
              placement="bottom"
              content={<TextV2 typographyVariant="label2">{tq`Transfer fee amount`}</TextV2>}
            >
              <IconV2
                size="sm"
                color="mono10"
              >
                <Info />
              </IconV2>
            </TooltipV2>
          </Flex>
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {feeFormatted}
          </TextV2>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex
            alignItems="center"
            gap={1}
          >
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {tq`Total Transfer`}
            </TextV2>
            <TooltipV2
              placement="bottom"
              content={<TextV2 typographyVariant="label2">{tq`Transfer fee amount`}</TextV2>}
            >
              <IconV2
                size="sm"
                color="mono10"
              >
                <Info />
              </IconV2>
            </TooltipV2>
          </Flex>
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {totalFormatted}
          </TextV2>
        </Flex>
      </Flex>
    </>
  );

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      overrides={transferDialogOverrides}
    >
      <DialogV2.Header paddingY={6}>
        {showDialogTitle && (
          <TextV2
            typographyVariant="hed6"
            color="mono14"
            textAlign="center"
            paddingTop={6}
          >
            {tq`Transfer to Bank`}
          </TextV2>
        )}
        <DialogV2.CloseButton />
      </DialogV2.Header>
      <DialogV2.Body overflow={[null, 'auto']}>
        {showStatusView && payoutStatus ? (
          <StatusBody
            status={payoutStatus}
            onClose={onClose}
            onStatusCloseClicked={telemetry?.onStatusCloseClicked}
          />
        ) : isPendingView && payoutFormProps ? (
          <TransferDialogBody
            flexDirection="column"
            textAlign="center"
          >
            <Flex
              justifyContent="center"
              marginBottom={5}
            >
              <TextV2
                textAlign="center"
                typographyVariant="body3"
                color="mono12"
              >
                {payoutFormProps.accountNumber
                  ? tq`You are about to transfer your cash balance to the account or card ending in ${payoutFormProps.accountNumber}. This transfer cannot be reversed.`
                  : tq`You are about to transfer your cash balance to your connected account or card. This transfer cannot be reversed.`}
              </TextV2>
            </Flex>
            {renderBalanceAndFeeBlock()}
            {!payoutFormProps.isPayoutAllowed && (
              <Flex
                alignItems="center"
                borderStyle="solid"
                borderWidth="1px"
                borderColor="negative3"
                borderRadius={3}
                backgroundColor="negative1"
                marginY={8}
                padding={5}
                width="100%"
              >
                <Box marginRight={6}>
                  <IconV2
                    size="lg"
                    color="negative5"
                  >
                    <Warning />
                  </IconV2>
                </Box>
                <Box>
                  <TextV2
                    typographyVariant="label2"
                    color="mono14"
                    textAlign="left"
                  >
                    {tq`Sorry! A minimum balance of $2 is needed to process a balance transfer.`}
                  </TextV2>
                </Box>
              </Flex>
            )}
            <Flex
              width="100%"
              justifyContent="center"
              gap={3}
              paddingBottom={6}
              flexDirection="column"
              alignItems="center"
              marginTop={8}
            >
              <ButtonV2
                size="lg"
                intent="neutral"
                shape="rounded"
                disabled={!payoutFormProps.isPayoutAllowed}
                loading={payoutFormProps.loadingPayoutRequest}
                onClick={() => {
                  const currentBalance = availableBalance?.valueInMajorUnits ?? 0;
                  telemetry?.onTransferBalanceClicked?.(currentBalance, availableBalance?.currencyCode ?? currencyCode ?? DEFAULT_CURRENCY, TRANSFER_FEE);
                  payoutFormProps.withdrawBalance();
                }}
                width={['100%', pxToRem(304)]}
                maxWidth="100%"
              >
                {tq`Yes, Transfer Balance`}
              </ButtonV2>
              <ButtonV2
                size="lg"
                intent="neutral"
                shape="rounded"
                variant="ghost"
                onClick={() => {
                  telemetry?.onNevermindClicked?.();
                  onClose();
                }}
                width={['100%', pxToRem(304)]}
                maxWidth="100%"
              >
                {tq`Nevermind`}
              </ButtonV2>
            </Flex>
          </TransferDialogBody>
        ) : (
          <TransferDialogBody
            flexDirection="column"
            textAlign="center"
          >
            {showAllyTransferPromo ? (
              <TextV2
                typographyVariant="body2"
                color="mono12"
                paddingBottom={8}
              >
                {tq`Before we connect a bank account, consider opening a joint account with Ally Bank.`}
              </TextV2>
            ) : null}

            {renderBalanceAndFeeBlock()}
            {showAllyTransferPromo && <AllyPromoBlock onOpenAccountClicked={telemetry?.onAllyOpenAccountClicked} />}
            <Flex
              width="100%"
              justifyContent="center"
              gap={3}
              paddingBottom={6}
              flexDirection="column"
              alignItems="center"
              marginTop={8}
            >
              {onConnectBank && (
                <ButtonV2
                  size="lg"
                  shape="rounded"
                  onClick={onConnectBank}
                  width={['100%', pxToRem(304)]}
                  maxWidth="100%"
                  intent="neutral"
                >
                  {tq`Connect Bank Account`}
                </ButtonV2>
              )}
            </Flex>
          </TransferDialogBody>
        )}
      </DialogV2.Body>
    </DialogV2>
  );
};
